import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useEffect, useState } from 'react';

import { FBoxContainer, FHButton, FHConfirmDialog } from '../../../base';
import FHExcelTable from '../../../base/FHExcelTable';
import { invoicePaymentTableColDefs } from './config';
import { FORTA_GRAY4, FORTA_BLUE } from '../../../../utils/colors';
import { claimsInvoiceActions } from '../../../../store';
import {
  InvoicePaymentStatusEnum,
  InvoicePaymentTypeEnum,
} from '../claim-info/claim-payments/constants';
import { areUserRolesIn, usDollarFormatter } from '../../../../helpers';
import { Roles } from '../../../../constants';

import type { RootState } from '../../../../store';
import type { IInvoicePayment } from '../../../../utils/types';

interface IInvoicePaymentsProps {
  isSentToGuardians?: boolean;
  invoiceLoading: boolean;
  invoiceId?: string;
  isEditable?: boolean;
  amountDue: number;
  invoicePayments: IInvoicePayment[];
  handlePDFDownload: () => void;
}

function InvoicePayments({
  isSentToGuardians,
  invoiceId,
  isEditable,
  invoicePayments,
  amountDue,
  handlePDFDownload,
}: IInvoicePaymentsProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isAddingPayment, setIsAddingPayment] = useState<boolean>(false);
  const [invoicePaymentData, setInvoicePaymentData] = useState<IInvoicePayment[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  useEffect(() => {
    const fillteredInvoicePayments =
      invoicePayments.filter((payment) => payment.status === InvoicePaymentStatusEnum.SUCCESS) ??
      [];
    setInvoicePaymentData(fillteredInvoicePayments);
    setIsAddingPayment(false);
  }, [invoicePayments]);

  const handleAddNewInvoicePayment = () => {
    const newPayment = {
      amount: null,
      mode: null,
      date_received: null,
      date_cleared: null,
      transaction_id: null,
      status: null,
      id: null,
    };
    setInvoicePaymentData((prev) => [...prev, newPayment]);
    setIsAddingPayment(true);
  };

  const handleSavePayment = async (data: IInvoicePayment) => {
    if (!invoiceId) return;
    if (data.id) {
      return dispatch(
        claimsInvoiceActions.updateInvoicePayment({
          getAccessTokenSilently,
          invoiceId,
          paymentId: data.id,
          data,
        }),
      );
    } else {
      return dispatch(
        claimsInvoiceActions.createInvoicePayment({
          getAccessTokenSilently,
          invoiceId,
          data,
        }),
      );
    }
  };

  const handleDeleteUnsavedPayment = () => {
    setInvoicePaymentData((prev) => prev.filter((it) => it.id));
    setIsAddingPayment(false);
  };

  const deleteInvoicePayment = async (rowData: IInvoicePayment) => {
    if (!invoiceId) return;
    if (!rowData?.id) {
      handleDeleteUnsavedPayment();
    } else {
      return await dispatch(
        claimsInvoiceActions.deleteInvoicePayment({
          getAccessTokenSilently,
          invoiceId,
          paymentId: rowData.id,
        }),
      );
    }
  };

  const handleGenerateFinalInvoice = () => {
    if (!invoiceId) return;
    setShowConfirmDialog(false);
    dispatch(claimsInvoiceActions.sendInvoice({ getAccessTokenSilently, invoiceId }));
  };

  return (
    <>
      <FBoxContainer sx={{ boxShadow: 'none' }}>
        <FHExcelTable
          isEditable={isEditable}
          colDefs={invoicePaymentTableColDefs}
          data={invoicePaymentData}
          maxNumericInputValue={1000000}
          deleteRowMessage="Are you sure you want to delete this line item?"
          saveRowMessage="Please ensure all information is correct before saving."
          onSave={handleSavePayment}
          onDelete={deleteInvoicePayment}
          discardUnsaved={() => {
            setInvoicePaymentData((prev) => prev.filter((it) => it.id));
            setIsAddingPayment(false);
          }}
          disableRowCriteria={(rowData: IInvoicePayment) =>
            rowData.mode === InvoicePaymentTypeEnum.ONLINE_PAYMENT
          }
        />
        {isEditable && (
          <Box sx={{ display: 'flex' }}>
            <IconButton
              sx={{
                borderRadius: '8px',
                color: FORTA_BLUE.primary,
              }}
              disabled={isAddingPayment}
              onClick={handleAddNewInvoicePayment}
            >
              <AddOutlinedIcon
                sx={{ width: 21, height: 21, mr: 0.5 }}
                data-testid="addNewInvoicePayment"
              />
              <Typography>Add</Typography>
            </IconButton>
          </Box>
        )}
      </FBoxContainer>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          mt: 3,
          textAlign: 'right',
        }}
      >
        <Box>
          <Typography mb={1}>Amount due</Typography>
          <Typography fontWeight={700} fontSize={32}>
            {usDollarFormatter.format(amountDue)}
          </Typography>
        </Box>
        {areUserRolesIn(user?.roles, [Roles.InvoiceCoordinator]) && (
          <Box mt={3}>
            <FHButton secondary onClick={handlePDFDownload} sx={{ mr: 3, borderRadius: '8px' }}>
              Download receipt
            </FHButton>
            <FHButton
              sx={{ borderRadius: '8px' }}
              disabled={isSentToGuardians || isAddingPayment}
              onClick={() => setShowConfirmDialog(true)}
              data-testid="finalInvoiceBtn"
            >
              {amountDue > 0 ? 'Send invoice' : 'Finalize invoice'}
            </FHButton>
            <FHConfirmDialog
              open={showConfirmDialog}
              title={amountDue > 0 ? 'Send Invoice' : 'Finalize Invoice'}
              titleStyles={{
                color: FORTA_BLUE.primary,
                fontWeight: 500,
              }}
              contentRenderer={() => (
                <Box px={2} py={4}>
                  <Typography fontSize={21} mb={3} textAlign={'center'}>
                    {amountDue > 0 && amountDue < 0.5
                      ? 'Cannot bill the parent for less than $0.50'
                      : amountDue > 0
                        ? `Send invoice for payment (Balance: ${usDollarFormatter.format(
                            amountDue,
                          )}) to guardian?`
                        : 'Finalize and send a no-balance invoice to the guardian?'}
                  </Typography>
                  <Typography textAlign={'center'} fontSize={20} fontWeight={700}>
                    {amountDue > 0 && amountDue < 0.5
                      ? 'Please adjust the added payments or write off the due amount.'
                      : amountDue > 0
                        ? 'No additional manual payments will be allowed after sending.'
                        : 'No additional edits will be allowed once finalized.'}
                  </Typography>
                </Box>
              )}
              dialogStyles={{
                padding: '16px',
                maxHeight: '85vh',
                height: 'auto',
                width: '680px',
                maxWidth: '680px',
              }}
              sx={{
                '& .MuiModal-backdrop': {
                  backdropFilter: 'blur(2px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.4)',
                },
              }}
              submittext={amountDue > 0 ? 'Send' : 'Finalize'}
              canceltext={'Cancel'}
              disableSubmitButton={amountDue > 0 && amountDue < 0.5}
              onClose={() => setShowConfirmDialog(false)}
              onSubmit={handleGenerateFinalInvoice}
              submitBtnTestId="finalizeConfirmationBtn"
            />
          </Box>
        )}
        {areUserRolesIn(user?.roles, [Roles.InvoiceCoordinator]) && isSentToGuardians && (
          <Typography
            textAlign="left"
            color={FORTA_GRAY4}
            mt={2}
            alignSelf={'flex-end'}
            sx={{ width: '600px' }}
          >
            The final invoice has been generated and sent to the guardian. If you need to make more
            changes, please contact an administrator to reset the invoice status.
          </Typography>
        )}
      </Box>
    </>
  );
}

export default InvoicePayments;
